import { useState } from "react";
import { motion } from "framer-motion";
import { Spade, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import BetControl from "../components/BetControl";
import Confetti from "../components/Confetti";
import CoinRain from "../components/CoinRain";
import { PlayingCard, CardBack } from "../components/PlayingCard";
import { ShareWin } from "../components/WinShareCard";
import { formatCoins } from "../lib/format";
import { playSpin, playWin, playLose } from "../lib/sound";

export default function TeenPatti() {
  const { t } = useLang();
  const { user, setCoins } = useAuth();
  const [bet, setBet] = useState(100);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [confetti, setConfetti] = useState(0);
  const [rain, setRain] = useState(0);

  const deal = async () => {
    if (busy) return;
    setBusy(true);
    setResult(null);
    playSpin();
    try {
      const { data } = await api.post("/games/teenpatti", { bet });
      setTimeout(() => {
        setResult(data);
        setCoins(data.coins);
        if (data.win > 0) { setConfetti(Date.now()); setRain(Date.now()); playWin(data.win); toast.success(`${t("youWon")} +${formatCoins(data.win)}!`); }
        else { playLose(); toast(`${t("youLost")} (-${formatCoins(bet)})`); }
      }, 550);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTimeout(() => setBusy(false), 600);
    }
  };

  const Row = ({ label, cards, hand, win, color }) => (
    <div className="flex flex-col items-center gap-2">
      <p className="text-xs uppercase tracking-wider text-slate-400">{label} {hand && <span className="text-amber-300 font-bold">· {hand}</span>}</p>
      <div className="flex gap-2">
        {cards ? cards.map((c, i) => <PlayingCard key={i} card={c} delay={i * 0.12} highlight={win} testid={`tp-${color}-card-${i}`} />)
          : [0, 1, 2].map((i) => <CardBack key={i} testid={`tp-${color}-back-${i}`} />)}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <Confetti fire={confetti} />
      <CoinRain fire={rain} />
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold gold-text">{t("teenPatti")}</h1>
        <p className="text-sm text-slate-400 mt-1">{t("teenPattiDesc")}</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <div className="mw-card p-6 flex flex-col items-center justify-center gap-6 min-h-[340px]" data-testid="tp-table">
          <Row label={t("dealerCards")} cards={result?.dealer} hand={result?.dealer_hand} win={result && !result.won} color="dealer" />
          {result && (
            <motion.p initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="font-display text-2xl font-extrabold" style={{ color: result.won ? "#00E676" : "#FF3366" }} data-testid="tp-result">
              {result.won ? `${t("youWon")} +${formatCoins(result.win)}` : `${t("youLost")} -${formatCoins(bet)}`}
            </motion.p>
          )}
          {result?.won && <ShareWin amount={result.win} game="teenpatti" />}
          <Row label={t("yourCards")} cards={result?.player} hand={result?.player_hand} win={result && result.won} color="player" />
        </div>

        <div className="mw-card p-6 space-y-5">
          <BetControl bet={bet} setBet={setBet} max={user?.coins} disabled={busy} />
          <div className="rounded-lg border border-amber-500/20 bg-black/30 py-2 text-center text-xs text-slate-400">
            {t("winPays")} <span className="font-mono-num text-amber-300 font-bold">1.95×</span>
          </div>
          <button onClick={deal} disabled={busy} data-testid="tp-deal-btn" className="w-full btn-gold rounded-xl py-3 text-lg flex items-center justify-center gap-2">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <Spade size={18} />} {t("deal")}
          </button>
        </div>
      </div>
    </div>
  );
}
